import * as React from 'react';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import type { LogoAsset } from '@/types/shared/logo';

const avatarVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center overflow-hidden bg-muted-hover text-muted-foreground',
  {
    variants: {
      size: {
        xxs: 'h-6 w-6 text-xs sm:h-4 sm:w-4 sm:text-[10px]',
        xs: 'h-8 w-8 text-sm sm:h-6 sm:w-6 sm:text-xs',
        sm: 'h-10 w-10 text-base sm:h-8 sm:w-8 sm:text-sm',
        md: 'h-12 w-12 text-lg sm:h-10 sm:w-10 sm:text-base',
        lg: 'h-14 w-14 text-xl sm:h-12 sm:w-12 sm:text-lg',
        xl: 'h-20 w-20 text-2xl sm:h-16 sm:w-16 sm:text-xl',
        '2xl': 'h-24 w-24 text-3xl sm:h-20 sm:w-20 sm:text-2xl',
        '3xl': 'h-28 w-28 text-4xl sm:h-24 sm:w-24 sm:text-3xl',
      },
      shape: {
        circle: 'rounded-full',
        squircle: 'rounded-squircle',
      },
    },
    defaultVariants: {
      size: 'md',
      shape: 'circle',
    },
  }
);

type AvatarProps = React.ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants> & {
    src?: string;
    logo?: LogoAsset;
    alt?: string;
    fallback?: React.ReactNode;
  };

// Mirrors ThemeProvider: the persisted color mode lives under `hydn-theme` and
// is read synchronously, so the correct logo renders on first paint — before
// ThemeProvider's effect adds the `dark` class to <html>.
function prefersDark() {
  try {
    return localStorage.getItem('hydn-theme') === 'dark';
  } catch {
    return document.documentElement.classList.contains('dark');
  }
}

function useThemeAwareSrc(logo?: LogoAsset, src?: string) {
  const [isDark, setIsDark] = React.useState(prefersDark);

  React.useEffect(() => {
    const root = document.documentElement;
    // The class mutation is the trigger; localStorage is the source of truth
    // (ThemeProvider writes it before flipping the class), so reading it here
    // keeps us in sync without flashing while the class lands.
    const update = () => setIsDark(prefersDark());
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  if (logo) return isDark ? (logo.dark ?? logo.light) : logo.light;
  return src;
}

function Avatar({
  className,
  size,
  shape,
  src,
  logo,
  alt = '',
  fallback,
  ...props
}: AvatarProps) {
  const displaySrc = useThemeAwareSrc(logo, src);

  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarVariants({ size, shape }), className)}
      {...props}
    >
      <AvatarPrimitive.Image
        src={displaySrc}
        alt={alt}
        className="h-3/4 w-3/4 object-contain"
      />
      <AvatarPrimitive.Fallback
        className="flex h-full w-full items-center justify-center font-medium"
        delayMs={0}
      >
        {fallback ?? alt.charAt(0).toUpperCase()}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export { Avatar, avatarVariants };
export type { AvatarProps };
